import { useMemo } from 'react';
import { AuthenticatedTemplate, UnauthenticatedTemplate, useMsal } from '@azure/msal-react';
import { RelayEnvironmentProvider } from 'react-relay';
import { RouterProvider } from '@tanstack/react-router';
import { ThemeProvider, ToastProvider } from '@pv-frontend/ui-components-web';
import { readEnv } from './env';
import { isAdmin } from './auth/admin-group';
import { createAccessTokenProvider } from './auth/access-token';
import { createAdminRelayEnvironment } from './relay/environment';
import { serviceStatusStore } from './relay/service-status-instance';
import { LoginScreen } from './screens/login-screen';
import { AccessDenied } from './screens/access-denied';
import { router } from './router/router';

const env = readEnv();

/**
 * The authenticated app. Refuses non-admin-group users, then builds the dedicated
 * Admin Relay environment (Entra token injected per request) and hands control to
 * the router. The environment is memoised per operator account.
 */
function AuthedApp() {
    const { instance, accounts } = useMsal();
    const account = accounts[0] ?? null;

    const environment = useMemo(() => {
        if (!account) return null;
        const acquireToken = createAccessTokenProvider(instance, account, env);
        return createAdminRelayEnvironment({
            apiUrl: env.adminApiUrl,
            acquireToken,
            status: serviceStatusStore,
        });
    }, [instance, account]);

    if (!account || !environment) return null;
    if (!isAdmin(account, env.adminGroupId)) return <AccessDenied />;

    return (
        <RelayEnvironmentProvider environment={environment}>
            <ToastProvider>
                <RouterProvider router={router} />
            </ToastProvider>
        </RelayEnvironmentProvider>
    );
}

/** Root composition: theme, then the Entra auth gate (login vs. authenticated app). */
export function App() {
    return (
        <ThemeProvider defaultColorScheme="dark">
            <AuthenticatedTemplate>
                <AuthedApp />
            </AuthenticatedTemplate>
            <UnauthenticatedTemplate>
                <LoginScreen />
            </UnauthenticatedTemplate>
        </ThemeProvider>
    );
}
